import { Loadable } from '@/components/loaders';
import { useWorkspaceAdmin } from '@/features/workspaces/hooks/useWorkspace';
import { FrontTable } from '@/lib/Tables';
import { Audience } from '@/models/audiences';
import { ConnectorFlowInstanceDTO } from '@/models/connectors';
import { DistributionChannel } from '@/models/distributionChannels';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AudienceDestinationTableCaption } from './AudienceDestinationTableCaption';
import { getDestinationTableColumns } from './helpers';

type AudienceDestinationTableProps = {
  destinations: DistributionChannel[];
  flowInstances: ConnectorFlowInstanceDTO[];
  audience?: Audience;
  isLoading: boolean;
};

export const AudienceDestinationTable: FC<AudienceDestinationTableProps> = ({ destinations, flowInstances, audience, isLoading }) => {
  const { t } = useTranslation();
  const isWorkspaceAdmin = useWorkspaceAdmin();

  const tableData = useMemo(() => {
    const combinedData = [
      ...destinations.map(dest => ({
        id: dest.id,
        name: dest.name,
        type: dest.destination,
        destination: dest.destination,
        createdOn: dest.createdOn,
        data: dest,
      })),
      ...flowInstances.map(flow => ({
        id: flow.id,
        name: `Custom Connector Flow`,
        type: 'Custom',
        destination: 'Custom Connector',
        createdOn: flow.createdAt,
        data: flow,
      })),
    ];
    return combinedData;
  }, [destinations, flowInstances]);

  const columns = useMemo(() => getDestinationTableColumns(t, audience, isWorkspaceAdmin), [t, audience, isWorkspaceAdmin]);

  return (
    <Loadable isLoading={isLoading}>
      <FrontTable
        caption={<AudienceDestinationTableCaption destinations={destinations} flowInstances={flowInstances} />}
        tableData={tableData}
        columns={columns}
        isLoading={false}
        notFound={t('audience_details:destinations.empty')}
      />
    </Loadable>
  );
};
